import { format } from 'date-fns';
import { sortBy } from 'lodash';
import { action, computed, observable } from 'mobx';
import { observer } from 'mobx-react';
import {
    autoinject,
    bind,
    Enums,
    FormStore,
    inject,
    Input,
    Loader,
    Modal,
    ModalHandle,
    NothingHere,
    objectToHumanString,
    SelectInput,
    TasksStore,
    toKeyArray,
    uuidv4generate,
} from 'Mri.Web.Common';
import * as React from 'react';
import { BsCardHeading } from '@react-icons/all-files/bs/BsCardHeading';
import { BsFileEarmark } from '@react-icons/all-files/bs/BsFileEarmark';
import { Button, Form, FormGroup } from 'reactstrap';
import { AccountStore } from '../Account/AccountStore';
import { AppStore } from '../Core/AppStore';
import { PartsStore } from './PartsStore';

interface ISelectOption {
    value: string;
    label: string;
}

export interface IPartStatusModalParams {
    partStatus: Mri.PartStatus.PartStatus;
    partGuid: string;
    readonly modalHandle: ModalHandle;
    readonly includeChildren?: boolean;
}

@autoinject
@observer
export class PartStatusModal extends React.Component<IPartStatusModalParams> {
    public render() {
        const { includeChildren } = this.props;

        return (
            <Modal
                title={this.title}
                size="lg"
                modalHandle={this.props.modalHandle}
                footer={this.footer}
                bodyClass="p-0 vh-50"
                onOpened={this.loadStatusLog}
            >
                {this.partStatusForm && !!this.partStatusForm!.globalErrors.length && (
                    <div className="alert-danger p-2">
                        <ul>
                            {this.partStatusForm!.globalErrors.map((e, idx) => (
                                <li key={idx}>{e}</li>
                            ))}
                        </ul>
                    </div>
                )}

                <div className="container-fluid">
                    <div className="row">
                        <div className="col-12 col-lg-6">
                            <Form>
                                <FormGroup>
                                    <label>
                                        <strong>Part Status</strong>
                                    </label>

                                    <SelectInput
                                        field={this.partStatusForm.fields.partState}
                                        options={this.partStateOptions}
                                        placeholder="Part State"
                                        required={true}
                                    />
                                </FormGroup>
                                <FormGroup>
                                    <label>Comments</label>

                                    <Input type="text" id="name" field={this.partStatusForm!.fields.comments} />
                                </FormGroup>
                            </Form>
                        </div>

                        <div className="col-12 overflow">
                            <div>
                                <table className="table table-sm">
                                    <thead>
                                        <tr>
                                            {includeChildren && (
                                                <>
                                                    <th className="text-nowrap" scope="col">
                                                        Serial #
                                                    </th>
                                                    <th className="text-nowrap" scope="col">
                                                        Part #
                                                    </th>
                                                    <th className="text-nowrap" scope="col">
                                                        Description
                                                    </th>
                                                </>
                                            )}
                                            <th scope="col">State</th>
                                            <th scope="col">Comments</th>
                                            <th scope="col">Reported Date</th>
                                            <th scope="col">Payload</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {includeChildren
                                            ? this.statusLogForTree?.map(this.renderStatusLogForTree)
                                            : this.statusLogs?.map(this.renderStatusLog)}
                                    </tbody>
                                </table>

                                <NothingHere
                                    loading={this.loading}
                                    empty={!this.statusLogs?.length}
                                    icon={<BsCardHeading />}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </Modal>
        );
    }

    @bind
    @action
    public async loadStatusLog() {
        this.loading = true;

        try {
            await this.partsStore.loadStatusLogAsync(this.props.partGuid, this.props.includeChildren);
            await this.partsStore.loadPartsFromGuid(this.props.partGuid, true);
        } catch (e: any) {
            this.appStore.setError(e);
        } finally {
            this.loading = false;
        }
    }

    public renderStatusLog(status: Mri.PartStatus.PartStatus) {
        return (
            <tr key={`${status.startDate}${status.partState}${status.userId}$[status.}`}>
                <td>{Enums.toString(Enums.PartState, status.partState) || '-'}</td>
                <td>{status.comments || '-'}</td>
                <td>{format(status.startDate, 'MMMM do yyyy, h:mm:ss a') || '-'}</td>

                <td>
                    <pre>
                        <code>{status.payload ? objectToHumanString(status.payload, true) : '-'}</code>
                    </pre>
                </td>
            </tr>
        );
    }

    public renderStatusLogForTree(entry: { part: Mri.Parts.Part; status: Mri.PartStatus.PartStatus }, key: any) {
        const { part, status } = entry;
        return (
            <tr key={key}>
                <td className="text-nowrap">{part.serialNumber}</td>
                <td className="text-nowrap">{part.partNumber}</td>
                <td>{part.description ?? '-'}</td>
                <td>{Enums.toString(Enums.PartState, status.partState) ?? '-'}</td>
                <td>{status.comments || '-'}</td>
                <td>{format(status.startDate, 'MMMM do yyyy, h:mm:ss a') ?? '-'}</td>

                <td>
                    <pre>
                        <code>{status.payload ? objectToHumanString(status.payload, true) : '-'}</code>
                    </pre>
                </td>
            </tr>
        );
    }

    @computed
    private get footer() {
        return (
            <Button color="primary" onClick={this.submitAsync} disabled={this.loading}>
                <Loader loading={this.loading} showSpinner message={'Updating Part Status...'} />
                {!this.loading && 'Update Part Status'}
            </Button>
        );
    }

    @computed
    private get partStateOptions() {
        return toKeyArray(Enums.PartState).map(key => {
            return {
                value: key,
                label: Enums.toString(Enums.PartState, key),
            };
        });
    }

    @bind
    @action
    private async submitAsync() {
        if (!this.partStatusForm) throw new Error('Form does not exist');

        this.partStatusForm.onSubmit();
        if (this.partStatusForm.validate()) {
            this.loading = true;
            this.partStatusForm.fields.startDate.setValue(new Date());
            const task = this.tasksStore.startSessionTask('Update Part Status');

            try {
                await this.partsStore.updateStatusAsync(this.partStatusForm.values, this.props.partGuid);
                task.complete();
                this.props.modalHandle.close();
            } catch (e: any) {
                if (e.modelState) {
                    this.partStatusForm.clearGlobalErrors();
                    this.partStatusForm.addErrors(e.modelState);
                }

                task.error(e);
            } finally {
                this.loading = false;
            }
        } else {
            console.log('invalid');
            this.loading = false;
        }
    }

    private title = (
        <span className="text-header fw-bold">
            <BsFileEarmark /> Edit Part Log
        </span>
    );

    @computed
    private get statusLogs() {
        const logs = this.partsStore.statusLogs.get(this.props.partGuid);
        return logs?.slice().sort((a, b) => {
            return b.startDate.valueOf() - a.startDate.valueOf();
        });
    }

    @computed
    private get statusLogForTree() {
        const logs = this.partsStore.getPartStatusLogsForTree(this.props.partGuid);
        return logs?.slice().sort((a, b) => {
            return b.status.startDate.valueOf() - a.status.startDate.valueOf();
        });
    }

    @observable private loading = false;

    @inject private readonly partsStore!: PartsStore;
    @inject private tasksStore!: TasksStore;
    @inject private appStore!: AppStore;
    @inject private readonly accountStore!: AccountStore;

    @observable private partStatusForm = new FormStore<Mri.PartStatus.PartStatus>({
        userId: this.accountStore.user!.id,
        startDate: null,
        partState: this.props.partStatus.partState,
        comments: null,
    });
}
